import React, { useEffect, useMemo, useState } from 'react';
import { ClienteConsolidado } from '../types';
import type { RecorteDaTela } from '../lib/perfisExportacao';
import { formatCurrency, differenceInDays, parseISO } from '../utils';
import { Search, ExternalLink, CalendarClock } from 'lucide-react';

interface Props {
  clientes: ClienteConsolidado[];
  onNavigate?: (tab: string, clientName?: string) => void;
  /** Reporta ao cabeçalho o que esta aba está mostrando, para o Excel sair igual. */
  onRecorteChange?: (recorte: RecorteDaTela) => void;
}

const PainelRecebimentos: React.FC<Props> = ({ clientes, onNavigate, onRecorteChange }) => {
  const [busca, setBusca] = useState('');

  // Expandir todas faturas dos clientes
  const faturasDaCarteira = useMemo(() => clientes.flatMap(c =>
    c.faturas.map(f => ({
      ...f,
      cliente: c.nome,
      interno: c.interno,
      risco: c.risco,
      score: c.score
    }))
  ), [clientes]);

  const todasFaturas = useMemo(
    () => faturasDaCarteira.filter(f => !busca || f.cliente.toLowerCase().includes(busca.toLowerCase()) || f.uc_codigo?.includes(busca)),
    [faturasDaCarteira, busca]);

  // A busca daqui não existe para o cabeçalho, onde fica o botão de exportar.
  const recorte: RecorteDaTela = useMemo(() => ({
    ids: todasFaturas.map(f => String(f.id)),
    descricao: busca.trim() ? `busca "${busca.trim()}"` : '',
    totalSemFiltro: faturasDaCarteira.length,
  }), [todasFaturas, busca, faturasDaCarteira.length]);

  useEffect(() => { onRecorteChange?.(recorte); }, [recorte, onRecorteChange]);

  const renderBadge = (diasAtraso: number) => {
    if (diasAtraso <= 15) return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700">Até 15d</span>;
    if (diasAtraso <= 30) return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700">Até 30d</span>;
    if (diasAtraso <= 60) return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-yellow-100 text-yellow-700">Até 60d</span>;
    return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700">Mais de 60d</span>;
  };

  return (
    <div className="p-6 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Painel Dinâmico de Recebimentos</h2>
          <p className="text-sm text-gray-500">Tabela analítica de faturas em aberto para ação imediata</p>
        </div>
        <div className="relative w-64">
          <input 
            type="text" 
            placeholder="Buscar fatura ou UC..." 
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-[#5F6C37] focus:border-[#5F6C37]"
          />
          <Search size={14} className="absolute left-3 top-2.5 text-gray-400" />
        </div>
      </div>

      <div className="flex-1 bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col">
        <div className="overflow-x-auto flex-1">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-gray-500 uppercase bg-gray-50 sticky top-0 z-10 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 font-bold">Cliente</th>
                <th className="px-4 py-3 font-bold">UC</th>
                <th className="px-4 py-3 font-bold text-right">Valor</th>
                <th className="px-4 py-3 font-bold">Vencimento</th>
                <th className="px-4 py-3 font-bold">Atraso</th>
                <th className="px-4 py-3 font-bold">Score</th>
                <th className="px-4 py-3 font-bold text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              {todasFaturas.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                    Nenhuma fatura encontrada.
                  </td>
                </tr>
              ) : (
                todasFaturas.map(f => {
                  const venc = f.vencimento_boleto || f.data_vencimento;
                  const atraso = venc ? differenceInDays(new Date(), parseISO(venc)) : 0;
                  return (
                    <tr key={f.id} className={`border-b border-gray-100 hover:bg-gray-50 ${f.interno ? 'opacity-60' : ''}`}>
                      <td className="px-4 py-3 font-semibold text-gray-800">
                        {f.cliente}
                        {f.interno && <span className="ml-2 text-[10px] text-gray-400 font-normal border border-gray-200 px-1 rounded">INTERNO</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{f.uc_codigo || '—'}</td>
                      <td className="px-4 py-3 text-right font-black text-[#5F6C37]">{formatCurrency(f.valor_boleto_vigor)}</td>
                      <td className="px-4 py-3 text-xs text-gray-600">
                        <div className="flex items-center gap-1.5">
                          <CalendarClock size={12} className="text-gray-400" />
                          {venc ? parseISO(venc).toLocaleDateString('pt-BR') : '—'}
                        </div>
                      </td>
                      <td className="px-4 py-3">{renderBadge(atraso)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-black
                          ${f.score === 'A' ? 'bg-green-100 text-green-700' : 
                            f.score === 'B' ? 'bg-blue-100 text-blue-700' : 
                            f.score === 'C' ? 'bg-yellow-100 text-yellow-700' : 
                            'bg-red-100 text-red-700'}`}>
                          {f.score}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          title="Detalhes da Fatura"
                          onClick={() => {
                            if (onNavigate) {
                              onNavigate('perfil', f.cliente);
                            }
                          }}
                          className="text-[#F6A937] hover:text-orange-500 transition-colors p-1 rounded hover:bg-orange-50"
                        >
                          <ExternalLink size={16} />
                        </button>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PainelRecebimentos;
